import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { api } from "@/lib/api";
import { LANDING } from "@/constants/testIds";

const feePreview = (b) => {
  if (b.monthly_fee == null) return null;
  const monthly = b.monthly_fee === 0 ? "No monthly fee" : `${b.monthly_fee} ${b.currency}/mo`;
  const wire = b.wire_fee != null ? ` · Wire ${b.wire_fee} ${b.currency}` : "";
  return `${monthly}${wire}`;
};

export const GlobalSearchBar = ({ enableShortcuts = true, autoFocusOnMount = false, onNavigate, showKbdHint = true, testIdPrefix = "" }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [nlResult, setNlResult] = useState(null);
  const [nlLoading, setNlLoading] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);
  const lastNlQueryRef = useRef(null);
  const navigate = useNavigate();
  const tid = (base) => (testIdPrefix ? `${testIdPrefix}-${base}` : base);

  useEffect(() => {
    if (autoFocusOnMount) inputRef.current?.focus();
  }, [autoFocusOnMount]);

  useEffect(() => {
    if (!enableShortcuts) return;
    const handleKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      } else if (e.key === "/" && !["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName) && !document.activeElement?.isContentEditable) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [enableShortcuts]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = query.trim();
    setNlResult(null);
    lastNlQueryRef.current = null;
    if (!trimmed) {
      setResults(null);
      setSuggestions([]);
      setOpen(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(() => {
      api
        .get(`/search?q=${encodeURIComponent(trimmed)}`)
        .then((r) => {
          setResults(r.data);
          setOpen(true);
          setActiveIndex(-1);
          if (r.data.banks_total === 0 && r.data.private_banks_total === 0 && looksLikeSentence(trimmed)) {
            runSmartSearch();
          }
        })
        .catch(() => setResults(null))
        .finally(() => setLoading(false));
      api
        .get(`/search/suggestions?q=${encodeURIComponent(trimmed)}`)
        .then((r) => setSuggestions(r.data?.suggestions || []))
        .catch(() => setSuggestions([]));
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const looksLikeSentence = (text) => text.trim().split(/\s+/).length >= 3;

  const runSmartSearch = () => {
    const trimmed = query.trim();
    if (!looksLikeSentence(trimmed) || nlLoading || lastNlQueryRef.current === trimmed) return;
    lastNlQueryRef.current = trimmed;
    setNlLoading(true);
    api
      .get(`/search/nl?q=${encodeURIComponent(trimmed)}`)
      .then((r) => setNlResult(r.data?.understood ? r.data : null))
      .catch(() => setNlResult(null))
      .finally(() => setNlLoading(false));
  };

  const flatItems = results
    ? [
        ...results.banks.map((b) => ({ type: "bank", ...b })),
        ...results.private_banks.map((b) => ({ type: "private_bank", ...b })),
        ...results.pages.map((p) => ({ type: "page", ...p })),
        ...results.regions.map((r) => ({ type: "region", ...r })),
        ...results.countries.map((c) => ({ type: "country", ...c })),
        ...results.fee_categories.map((f) => ({ type: "fee_category", ...f })),
      ]
    : [];

  const closeAndReset = () => {
    setOpen(false);
    setQuery("");
    setResults(null);
    setNlResult(null);
    setSuggestions([]);
  };

  const goTo = (item) => {
    if (item.type === "bank" || item.type === "private_bank") navigate(`/banks/${item.id}`);
    else if (item.type === "page") navigate(item.path);
    else if (item.type === "region") navigate(`/banks?region=${item.key}`);
    else if (item.type === "country") navigate(item.has_page ? `/countries/${item.slug}` : `/banks?q=${encodeURIComponent(item.name)}`);
    else if (item.type === "fee_category") navigate(`/fees/${item.slug}`);
    closeAndReset();
    onNavigate?.();
  };

  const viewAllBanks = () => {
    navigate(`/banks?q=${encodeURIComponent(query.trim())}`);
    closeAndReset();
    onNavigate?.();
  };

  const applySuggestion = (text) => {
    setQuery(text);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    // Only fall through to the non-deterministic natural-language search when the deterministic
    // keyword/intent search (e.g. "cheapest private bank in Hong Kong") found nothing at all -
    // never bypass it just because no dropdown item is highlighted yet.
    if (e.key === "Enter" && looksLikeSentence(query) && !hasResults) {
      e.preventDefault();
      runSmartSearch();
      return;
    }
    if (!open || flatItems.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, flatItems.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0) goTo(flatItems[activeIndex]);
      else goTo(flatItems[0]);
    } else if (e.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  const hasResults = results && flatItems.length > 0;
  const showNoResults = results && !loading && flatItems.length === 0 && query.trim() && !nlResult?.understood;
  let idx = -1;

  return (
    <div ref={containerRef} className="relative w-full max-w-2xl">
      <div
        className={`flex items-center gap-3 bg-white border rounded-full px-5 py-3.5 transition-all duration-200 ${
          open ? "border-[#059669] ring-2 ring-[#059669]/20 shadow-lg" : "border-black/15 shadow-sm hover:shadow-md"
        }`}
      >
        <Search size={18} className={open ? "text-[#059669] shrink-0" : "text-[#646973] shrink-0"} />
        <input
          ref={inputRef}
          data-testid={tid(LANDING.searchInput)}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results && setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Search any bank, or ask 'cheapest wire fee in Europe'…"
          className="flex-1 bg-transparent outline-none border-none text-base text-[#0F1115] placeholder-[#94A3B8] min-w-0"
        />
        {query && (
          <button
            type="button"
            data-testid={tid(LANDING.searchClearBtn)}
            onClick={() => {
              setQuery("");
              setResults(null);
              setSuggestions([]);
              inputRef.current?.focus();
            }}
            className="text-[#646973] hover:text-[#0F1115] transition-colors shrink-0"
            aria-label="Clear search"
          >
            <X size={16} />
          </button>
        )}
        {showKbdHint && <kbd className="hidden sm:inline-block text-xs text-[#94A3B8] border border-black/10 bg-black/[0.03] px-2 py-1 rounded shrink-0">⌘K</kbd>}
      </div>

      {open && (
        <div
          data-testid={tid(LANDING.searchDropdown)}
          className="absolute left-0 right-0 top-full mt-2 z-50 bg-white border border-black/10 rounded-2xl shadow-2xl overflow-hidden max-h-[460px] overflow-y-auto text-left"
        >
          {suggestions.length > 0 && (
            <div data-testid={tid(LANDING.searchSuggestionsSection)} className="border-b border-black/[0.06] py-1 bg-black/[0.015]">
              <p className="px-4 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">Suggested Searches</p>
              {suggestions.map((s, i) => (
                <button
                  key={`sugg-${i}`}
                  type="button"
                  data-testid={tid(LANDING.searchSuggestionItem)}
                  onClick={() => applySuggestion(s)}
                  className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-[#0F1115] hover:bg-black/[0.03] transition-colors text-left"
                >
                  <Search size={13} className="text-[#94A3B8] shrink-0" />
                  <span className="truncate">{s}</span>
                </button>
              ))}
            </div>
          )}
          {nlLoading && (
            <p data-testid={tid(LANDING.searchSmartLoading)} className="px-4 py-4 text-sm text-[#059669] flex items-center gap-2">
              <span className="inline-block w-3 h-3 border-2 border-[#059669] border-t-transparent rounded-full animate-spin" />
              Understanding your question…
            </p>
          )}
          {nlResult?.understood && (
            <div data-testid={tid(LANDING.searchSmartSection)} className="border-b border-black/[0.06] py-1 bg-[#059669]/[0.03]">
              <p className="px-4 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-[#059669]">Smart Match</p>
              <p data-testid={tid(LANDING.searchSmartSummary)} className="px-4 pb-2 text-xs text-[#646973]">{nlResult.summary}</p>
              {nlResult.results.map((b) => (
                <ResultItem
                  key={`nl-${b.id}`}
                  testId={tid(LANDING.searchSmartItem)}
                  active={false}
                  onClick={() => goTo({ type: "bank", ...b })}
                  badge={b.country}
                  badgeClass="bg-[#059669]/10 text-[#059669] border-[#059669]/30"
                >
                  {b.name}
                </ResultItem>
              ))}
            </div>
          )}
          {loading && <p className="px-4 py-4 text-sm text-[#646973]">Searching…</p>}
          {!loading && results?.intent && hasResults && (
            <p
              data-testid={tid(LANDING.searchIntentBanner)}
              className="px-4 py-2 text-xs font-semibold text-[#059669] bg-[#059669]/[0.05] border-b border-black/[0.06]"
            >
              Showing {results.intent.type === "cheapest" ? "cheapest" : "most expensive"}{" "}
              {results.intent.bank_type === "private" ? "private banks" : results.intent.bank_type === "retail" ? "retail banks" : "banks"}
              {results.intent.scope_label ? ` in ${results.intent.scope_label}` : ""}, sorted by fee score
            </p>
          )}
          {showNoResults && (
            <p data-testid={tid(LANDING.searchNoResults)} className="px-4 py-4 text-sm text-[#646973]">
              No matches for "{query}". Try a bank name, country, or fee type.
            </p>
          )}
          {hasResults && (
            <>
              {results.banks.length > 0 && (
                <ResultGroup label="Retail Banks">
                  {results.banks.map((b) => {
                    idx += 1;
                    return (
                      <ResultItem key={b.id} testId={tid(LANDING.searchResultItem)} active={idx === activeIndex} onClick={() => goTo({ type: "bank", ...b })} badge="Retail" badgeClass="bg-[#059669]/10 text-[#059669] border-[#059669]/30" subtitle={feePreview(b)}>
                        {b.name} <span className="text-[#94A3B8]">— {b.country}</span>
                      </ResultItem>
                    );
                  })}
                  {!results.intent && results.banks_total > results.banks.length && (
                    <button
                      type="button"
                      data-testid={tid(LANDING.searchViewAllBtn)}
                      onClick={viewAllBanks}
                      className="w-full text-left px-4 py-2 text-xs font-semibold text-[#059669] hover:bg-black/[0.03] transition-colors"
                    >
                      View all {results.banks_total} retail bank matches →
                    </button>
                  )}
                </ResultGroup>
              )}
              {results.private_banks.length > 0 && (
                <ResultGroup label="Private Banking">
                  {results.private_banks.map((b) => {
                    idx += 1;
                    return (
                      <ResultItem key={b.id} testId={tid(LANDING.searchResultItem)} active={idx === activeIndex} onClick={() => goTo({ type: "private_bank", ...b })} badge="Private" badgeClass="bg-[#D4AF37]/10 text-[#B8912E] border-[#D4AF37]/40" subtitle={feePreview(b)}>
                        {b.name} <span className="text-[#94A3B8]">— {b.country}</span>
                      </ResultItem>
                    );
                  })}
                </ResultGroup>
              )}
              {results.pages.length > 0 && (
                <ResultGroup label="Site Sections">
                  {results.pages.map((p) => {
                    idx += 1;
                    return (
                      <ResultItem key={p.key} testId={tid(LANDING.searchResultItem)} active={idx === activeIndex} onClick={() => goTo({ type: "page", ...p })} badge="Page" badgeClass="bg-slate-500/10 text-slate-600 border-slate-500/30">
                        {p.label}
                      </ResultItem>
                    );
                  })}
                </ResultGroup>
              )}
              {results.regions.length > 0 && (
                <ResultGroup label="Regions">
                  {results.regions.map((r) => {
                    idx += 1;
                    return (
                      <ResultItem key={r.key} testId={tid(LANDING.searchResultItem)} active={idx === activeIndex} onClick={() => goTo({ type: "region", ...r })} badge="Region" badgeClass="bg-blue-500/10 text-blue-600 border-blue-500/30">
                        {r.label}
                      </ResultItem>
                    );
                  })}
                </ResultGroup>
              )}
              {results.countries.length > 0 && (
                <ResultGroup label="Countries">
                  {results.countries.map((c) => {
                    idx += 1;
                    return (
                      <ResultItem key={c.slug} testId={tid(LANDING.searchResultItem)} active={idx === activeIndex} onClick={() => goTo({ type: "country", ...c })} badge="Country" badgeClass="bg-purple-500/10 text-purple-600 border-purple-500/30">
                        {c.name} {c.has_page && <span className="text-[#94A3B8]">— {c.bank_count} banks</span>}
                      </ResultItem>
                    );
                  })}
                </ResultGroup>
              )}
              {results.fee_categories.length > 0 && (
                <ResultGroup label="Fee Categories">
                  {results.fee_categories.map((f) => {
                    idx += 1;
                    return (
                      <ResultItem key={f.key} testId={tid(LANDING.searchResultItem)} active={idx === activeIndex} onClick={() => goTo({ type: "fee_category", ...f })} badge="Fee" badgeClass="bg-amber-500/10 text-amber-600 border-amber-500/30">
                        {f.label}
                      </ResultItem>
                    );
                  })}
                </ResultGroup>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const ResultGroup = ({ label, children }) => (
  <div className="border-b border-black/[0.06] last:border-b-0 py-1">
    <p className="px-4 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">{label}</p>
    {children}
  </div>
);

const ResultItem = ({ onClick, badge, badgeClass, children, active, testId, subtitle }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`w-full flex items-center justify-between gap-3 px-4 py-2.5 text-sm text-[#0F1115] transition-colors text-left ${active ? "bg-[#059669]/[0.06]" : "hover:bg-black/[0.03]"}`}
  >
    <span className="min-w-0">
      <span className="truncate block">{children}</span>
      {subtitle && <span data-testid="search-result-fee-preview" className="block text-xs text-[#94A3B8] truncate mt-0.5">{subtitle}</span>}
    </span>
    <span className={`shrink-0 text-[10px] font-bold uppercase tracking-wider border px-2 py-0.5 rounded-full ${badgeClass}`}>{badge}</span>
  </button>
);
